import React, { useEffect, useState, useMemo } from 'react';
import { Header, Message, Table, Icon, Label, Input, useFrontier, Button, Modal, Checkbox, Loader } from 'react-frontier2';
import { DeleteAssetModal, ErrorHeader, UploadAssetButton, UserLog } from '../../components';
import { useParams, Link } from 'react-router-dom';
import { BankAccount, SetLoading } from '@arema/components/Classes';
import { addCommas, bindClick, bindFormChange, dashify, fromUnix, fromUnixLocal, getNowLocal, getParticipant, uniqueArray } from '@arema/components/Util';
import { AssetFile, AssetType, PDVDepositTypes } from '../../AdminClasses';
import { useTitle } from '@arema/components/Hooks';
import { useUser } from '../../AdminHooks';
import { useTranslation } from 'react-i18next';
import CreateDepositModal from '../pdv_concilia/CreateDepositModal';
import API from '../../API';
import UserAccess from '../../UserAccess';
import classNames from 'classnames';

interface EventPayout {
	payout_id: number,
	event_id: number,
	event_name: string
	payout_type: number,
	payout_type_name: number,
	payout_method: number,
	bank_account: number,
	completed: boolean,
	approved: boolean,
	rejected: boolean,
	cancelled: boolean,
	comments: string,
	reject_feedback: string,
	legacy_id: number,
	user_creator: number,
	user_creator_name?: string,
	user_approved: number,
	user_approved_name?: string,
	user_completed: number,
	user_completed_name?: string,
	user_cancelled: number,
	user_cancelled_name?: string,
	date_scheduled: number,
	date_created: number,
	date_cancelled: number,
	date_approved: number,
	date_completed: number,
	dates: {
		date_id: number,
		date: number,
		city: string,
		amount: number,
		cancelled: boolean,
	}[],
	account: BankAccount,
	assets: AssetFile[],
	deposits: {
		deposit_id: number,
		deposit_type: PDVDepositTypes,
		pdv_id: number,
		pdv_name: string,
		deleted: boolean,
		date_deposit: number,
		date_created: number,
	}[],
}

enum ShownModal {
	APPROVE_COMPLETE = 1,
	REJECT = 2,
	EVIDENCE_DELETE = 3,
	RESET_PAYOUT = 3,
	DOWNLOAD_STP = 10,
	VIEW_DEPOSIT = 11,
}

var PayoutView = ()=>{
	var { t } = useTranslation();
	var { id } = useParams();
	var { user, hasAccess } = useUser();
	var { setTitle } = useTitle();
	var { Toast } = useFrontier();
	var [payout, setPayout] = useState<EventPayout>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [modalConfirm, setModalConfirm] = useState<boolean>(false);
	var [shownModal, setShownModal] = useState<ShownModal>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [rejectComments, setRejectComments] = useState<string>('');
	var [loadingStp, setLoadingStp] = useState<boolean>(false);
	var [deletingAsset, setDeletingAsset] = useState<AssetFile>(null);
	var [viewDeposit, setViewDeposit] = useState<number>(null);
	var [completeForm, setCompleteForm] = useState<{
		stp: boolean,
		completed: boolean
	}>(null);

	useEffect(()=>{
		setTitle(`Liquidación ${id}`);
		if(!Number.isNaN(id) && payout === null){
			API.getPayout(parseInt(id), true, true).then(res=>{
				if(res.error) return setLoadError(res.message);
				setPayout(res.data);
			}).catch(err=>{
				return setLoadError('Hubo un error inesperado cargando la liquidación. (LCL-1)');
			});
		}
	}, []);

	var ACCESS = useMemo(()=>{
		return {
			approve: hasAccess(UserAccess.CONTABILIDAD.APPROVE_PAYOUT),
			complete: hasAccess(UserAccess.CONTABILIDAD.SET_PAYOUT_COMPLETE),
			cancel: hasAccess(UserAccess.CONTABILIDAD.CANCEL_PAYOUT),
			stp: hasAccess(UserAccess.CONTABILIDAD.DOWNLOAD_STP_FILE),
			upload_evidence: hasAccess(UserAccess.CONTABILIDAD.UPLOAD_PAYOUT_FILE),
			remove_evidence: hasAccess(UserAccess.CONTABILIDAD.DELETE_PAYOUT_FILE),
		}
	}, [])

	var viewing_deposit = useMemo(()=>{
		if(!payout || !payout.deposits || !viewDeposit) return null;
		return payout.deposits.find(a=>a.deposit_id===viewDeposit);
	}, [viewDeposit, payout]);

	if(loadError){
		return <ErrorHeader text='Error' error={loadError} />
	}
	if(!payout){
		return <Header loading text='Cargando liquidación' />
	}

	var showModal = (modal: ShownModal)=>{
		return ()=>{
			setCompleteForm({
				stp: false,
				completed: false,
			})
			setModalConfirm(false);
			setShownModal(modal);
		}
	}

	var approvePayout = (setLoading: SetLoading)=>{
		if(!ACCESS.approve){
			setShownModal(null);
			return Toast.error('No tienes acceso a aprobar liquidaciones.');
		}
		setLoading(true);
		API.approvePayouts([payout.payout_id]).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Se ha aprobado la liquidación');
			setShownModal(null);
			var py = { ...payout };
			py.approved = true;
			py.user_approved = user.user_id;
			py.user_approved_name = user.name;
			py.date_approved = getNowLocal().toSeconds();
			if(!py.bank_account){
				py.completed = true;
			}
			setPayout(py);
		}).catch(err=>{
			Toast.error('Hubo un error inesperado aprobando la liquidación (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var rejectPayout = (setLoading: SetLoading)=>{
		if(!ACCESS.cancel){
			setShownModal(null);
			return Toast.error('No tienes acceso a cancelar liquidaciones.');
		}
		if(!modalConfirm) return;
		if(!rejectComments || rejectComments.length < 5){
			return setModalPrompts(['Favor de ingresar comentarios de la cancelación.']);
		}
		setModalPrompts(null);
		setLoading(true);
		API.rejectPayouts([payout.payout_id], rejectComments).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se han rechazado las liquidaciones.');
			setShownModal(null);
			var py = { ...payout };
			py.cancelled = true;
			py.rejected = true;
			py.reject_feedback = rejectComments;
			py.user_cancelled = user.user_id;
			py.user_cancelled_name = user.name;
			py.date_cancelled = getNowLocal().toSeconds();
			if(py.deposits){
				for(var i of py.deposits){
					i.deleted = true;
				}
			}
			setPayout(py);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado rechazando la liquidación (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var resetPayout = (setLoading: SetLoading)=>{
		if(!ACCESS.cancel || !modalConfirm) return;
		setModalPrompts(null);
		setLoading(true);
		API.resetPayouts([payout.payout_id]).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setShownModal(null);
			setPayout({
				...payout,
				completed: false
			});
			Toast.success(t('common.changes_saved'));
		}).catch(err=>{
			return setModalPrompts([t('common.unexpected_code', { code: 'LCL-PYVW-RTPY-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var completePayout = (force: boolean = false)=>{
		return (setLoading: SetLoading)=>{
			if(!ACCESS.complete){
				setShownModal(null);
				return Toast.error('No tienes acceso a completar liquidaciones.');
			}
			if(!completeForm || (!completeForm.completed && !completeForm.stp)){
				return setModalPrompts(['Favor de seleccionar descarga STP o ya depositado.']);
			}
			setModalPrompts(null);
			if(completeForm.stp && !force){
				return showSTP(setLoading);
			}

			setLoading(true);
			API.completePayouts([payout.payout_id]).then(res=>{
				if(res.error) return Toast.error(res.message);
				Toast.success('Se han completado las liquidaciones');
				setShownModal(null);

				var py = { ...payout };
				py.completed = true;
				py.rejected = false;
				py.cancelled = false;
				py.user_completed = user.user_id;
				py.user_completed_name = user.name;
				py.date_completed = getNowLocal().toSeconds();
				setPayout(py);
			}).catch(err=>{
				Toast.error('Hubo un error inesperado completando las liquidaciónes (LCL-1)');
			}).finally(()=>{
				setLoading(false);
			})
		}
	}

	var showSTP = (setLoading: SetLoading)=>{
		if(!ACCESS.stp){
			setShownModal(null);
			return Toast.error('No tienes acceso a descargar el archivo STP.');
		}
		setModalPrompts(null);
		setShownModal(ShownModal.DOWNLOAD_STP);
		setLoadingStp(true);
		API.downloadPayoutZip([payout.payout_id]).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado descargando el archivo STP. (LCL-1)']);
		}).finally(()=>{
			setLoadingStp(false);
		});
	}

	var showDeleteEvidence = (evidence: AssetFile)=>{
		return ()=>{
			if(!hasAccess(UserAccess.CONTABILIDAD.UPLOAD_PAYOUT_FILE)) return;
			setModalPrompts(null);
			setDeletingAsset(evidence);
			showModal(ShownModal.EVIDENCE_DELETE)();
		}
	}

	var onCompleteFormChange = bindFormChange(completeForm, setCompleteForm);
	var dates = payout.dates.filter(a=>!a.cancelled);
	var payout_total = dates.reduce((a, b)=>a + b.amount, 0) || 0;
	var cities = payout.dates ? uniqueArray(payout.dates.map(a=>a.city)) : [];
	var unix_now = getNowLocal().toSeconds();
	var bank_participant = payout.account && payout.account.bank_participant ? getParticipant(payout.account.bank_participant) : null;

	return <div>
		{!payout.approved && !payout.cancelled && !payout.completed && (
			<Message style={{ margin: 'auto', marginBottom: 10, maxWidth: 600 }} type='warning' centered>
				<div className="header">Liquidación en aprobación</div>
				Para poder realizar la liquidación, un usuario administrativo tendrá que aprobar la liquidación. {' '}
				{payout.bank_account ? 'Una vez aprobada se podrá hacer la transferencia bancaria si es necesaria.' : 'Una vez aprobada, ya que no requiere transferencia bancaria se marcará como completada.'}
			</Message>
		)}
		<Table details style={{ maxWidth: 600, margin: 'auto' }}>
			<Table.Head title={`Liquidación ${id}`} />
			<Table.Body>
				<Table.Row selectable data={['Evento', payout.event_name]} as={Link} compact to={`/events/${payout.event_id}`} />
				<Table.Row data={[`Ciudad${cities.length == 1 ? '' : 'es'}`, cities]} />
				<Table.Row data={['Tipo', payout.payout_type_name]} />
				{!!payout.legacy_id && (
					<Table.Row style={{ color: 'brown' }} data={['Migración T2', <>
						<span className="fr mono" style={{ color: 'brown' }}>{payout.legacy_id}</span>
						<div className="meta">
							<Link className="fr tiny red button" to={`https://admin.arema.mx/conciliacion/liquidaciones/${payout.legacy_id}`} target='_blank'>Ver</Link>
						</div>
					</>]} />
				)}
				<Table.Row data={['Cantidad', <b style={{ fontSize: 20 }}>{addCommas(payout_total)}</b>]} />
				<Table.Row data={['Fechas', dates.length]} />
				<Table.Row data={['Estatus', <>
					<div className={classNames('fr label', {
						red: payout.rejected || payout.cancelled,
						green: !payout.rejected && !payout.cancelled && payout.completed,
						yellow: !payout.rejected && !payout.cancelled && !payout.completed
					})} style={{ fontWeight: 'bold' }}>
						<i className={classNames('icon', {
							remove: payout.rejected || payout.cancelled,
							checkmark: !payout.rejected && !payout.cancelled && payout.completed,
							clock: !payout.rejected && !payout.cancelled && !payout.completed,
						})}></i>
						{
							payout.rejected ? 'Rechazada' :
							payout.cancelled ? 'Cancelada' :
							payout.completed ? 'Completada' :
							payout.approved ? 'Aprobada' : 'En aprobación'
						}
					</div>
				</>]} />
				{!!payout.date_scheduled && (
					<Table.Row data={['Agendado', <div className={classNames({
						red: (!payout.cancelled && !payout.completed && payout.date_scheduled < unix_now),
					})}>
						{(!payout.cancelled && !payout.completed && payout.date_scheduled < unix_now) && (
							<i className="exclamation circle icon"></i>
						)}
						<i className="calendar icon"></i>
						{fromUnix(payout.date_scheduled).toFormat('dd/MMM/yy')}
					</div>]} />
				)}
				{payout.comments && payout.comments.length > 0 ? (
					<Table.Row data={['Comentarios', payout.comments]} />
				) : null}
			</Table.Body>
			{!payout.cancelled && (
				<Table.Footer
					actions={(!payout.approved && !payout.completed && !payout.cancelled && ACCESS.approve) ? <>
						<Button text='Rechazar' color='red' onClick={showModal(ShownModal.REJECT)} />
						<Button text='Aprobar' color='green' onClick={showModal(ShownModal.APPROVE_COMPLETE)} />
					</> : !payout.completed ? <>
						{ACCESS.cancel && <Button text='Cancelar' basic onClick={showModal(ShownModal.REJECT)} />}
						{ACCESS.complete && <Button text='Completar' color='green' onClick={showModal(ShownModal.APPROVE_COMPLETE)} />}
					</> : ACCESS.cancel ? (
						<Button text='Regresar a pendiente' basic onClick={showModal(ShownModal.RESET_PAYOUT)}/>
					) : null} 
				/>
			)}
		</Table>
		{!!payout.rejected && (
			<Message centered type='error' style={{ maxWidth: 600, margin: 'auto', marginTop: 15 }}>
				<div className="header">Liquidación rechazada</div>
				El equipo administrativo ha rechazado la liquidación, se ha proporcionado el siguiente feedback:
				<Message style={{ marginTop: 15, backgroundColor: '#ffefef' }} type='error'>
					{payout.reject_feedback}
				</Message>
			</Message>
		)}
		{!!payout.completed && !payout.cancelled && (
			<Table style={{ margin: 'auto', marginTop: 15, maxWidth: 600 }}>
				<Table.Head
					title='Evidencias'
					centeredIndexes={[2]}
					collapsingIndexes={[1, 2]}
					data={[['Archivo', 'Fecha', <Icon name="ellipsis-h" />]]}
					titleRight={ACCESS.upload_evidence && (
						<UploadAssetButton
							data={{ payouts: [payout.payout_id] }}
							typeId={AssetType.PDF}
							onUpload={a=>{
								setPayout(p=>({ ...p, assets: [ ...p.assets, a ] }))
							}}
						/>
					)}
				/>
				<Table.Body>
					{(payout.assets && payout.assets.length>0) ? payout.assets.map(a=>(
						<Table.Row key={`evi-${a.asset_id}`} collapsingIndexes={[1, 2]} centeredIndexes={[2]} data={[
							a.asset_name,
							fromUnixLocal(a.date_created).toFormat('dd/MMM/yy HH:mm'),
							<Button icon iconName="ellipsis-h" size="tiny" menu={[
								{ text: 'Descargar', as: Link, to: a.download_url, target: '_blank' },
								{ text: 'Eliminiar', onClick: showDeleteEvidence(a), disabled: !ACCESS.remove_evidence }
							]} />
						]} />
					)) : (
						<Table.Cell empty row colSpan={5}>
							No hay evidencias en esta liquidación
						</Table.Cell>
					)}
				</Table.Body>
			</Table>
		)}
		{!!payout.deposits && payout.deposits.length>0 && (
			<Table style={{ margin: 'auto', marginTop: 15, maxWidth: 600 }}>
				<Table.Head title='Depositos' />
				<Table.Body>
					<Table.Row header>
						<Table.Cell centered colSpan={100} style={{ textTransform: 'none' }}>
							Esta liquidación está asociada a los siguientes depósitos de PDV
						</Table.Cell>
					</Table.Row>
					<Table.Row header collapsingIndexes={[0, 1, 3, 4, 5]} data={['ID', 'PDV', 'Nombre', 'Estatus', 'Fecha', '']} />
					{payout.deposits.map(a=>(
						<Table.Row
							key={`DEP-${a.deposit_id}`}
							collapsingIndexes={[0, 1, 3, 4, 5]}
							data={[
								a.deposit_id,
								a.pdv_id,
								a.pdv_name,
								<Label color={a.deleted ? 'red' : 'green'}>
									{a.deleted ? 'Cancelada' : 'Verificada'}
								</Label>,
								fromUnix(a.date_deposit || a.date_created).toFormat('dd/MMM/yy'),
								<Button size='tiny' icon iconName='ellipsis-h' menu={[
									{ text: 'Ver deposito', onClick: ()=>{
										setViewDeposit(a.deposit_id);
										setShownModal(ShownModal.VIEW_DEPOSIT);
									}},
									{ text: 'Ver PDV', as: Link, to: `/pdv/${a.pdv_id}/conciliacion`, target: '_blank' }
								]} />
							]}
						/>
					))}
				</Table.Body>
			</Table>
		)}

		{!!payout.bank_account && (
			<Table details style={{ margin: 'auto', maxWidth: 600, marginTop: 15 }}>
				<Table.Head title='Cuenta bancaria' />
				<Table.Body>
					<Table.Row data={['ID', payout.account.account_id]} />
					<Table.Row data={['Propietario', payout.account.name_account]} />
					<Table.Row data={['Correo', payout.account.email_account]} />
					{!!payout.account.rfc && (
						<Table.Row data={['RFC', payout.account.rfc]} />
					)}
					{!!payout.account.curp && (
						<Table.Row data={['CURP', payout.account.curp]} />
					)}
					{!!payout.account.postal_code && (
						<Table.Row data={['Código postal', payout.account.postal_code]} />
					)}
					<Table.Divider />
					<Table.Row data={['Tipo cuenta', <>
						<i className={`${payout.account.clabe ? 'landmark' : 'credit card'} icon`}></i>
						{payout.account.clabe ? 'Cuenta bancaria' : 'Tarjeta bancaria'}
					</>]} />
					<Table.Row data={['Banco', <>
						{bank_participant ? bank_participant.bank_name : payout.account.bank}
						{bank_participant && (
							<div className="meta">P: {bank_participant.participant}</div>
						)}
					</>]} />
					<Table.Row data={[
						payout.account.clabe ? 'Cuenta bancaria' : 'Tarjeta bancaria',
						payout.account.clabe ? dashify(payout.account.clabe, 6, ' ') : dashify(payout.account.card_number, 4, ' '),
					]} />
				</Table.Body>
			</Table>
		)}
		{!!payout.dates && payout.dates.length > 0 && (
			<Table style={{ margin: 'auto', marginTop: 15, maxWidth: 600 }}>
				<Table.Head title='Calendarios' data={[['ID', 'Fecha', 'Ciudad', 'Estatus', 'Cantidad']]} />
				<Table.Body>
					{payout.dates.sort((a, b)=>((a.cancelled ? 1 : 0) - (b.cancelled ? 1 : 0))).map(a=>(
						<Table.Row key={`dt-${a.date_id}`} collapsingIndexes={[0, 2, 3, 4]} data={[
							a.date_id,
							fromUnix(a.date).toFormat('dd/MMM/yy HH:mm'),
							a.city,
							<Label color={classNames({
								green: !payout.cancelled && payout.completed,
								yellow: !payout.cancelled && !payout.completed && !a.cancelled,
								red: payout.cancelled || a.cancelled
							}) as any}>
								{(payout.cancelled || a.cancelled) ? 'Cancelado' : payout.completed ? 'Completado' : 'Pendiente'}
							</Label>,
							addCommas(a.amount)
						]} />
					))}
				</Table.Body>
			</Table>
		)}

		<UserLog user={payout} style={{ margin: 'auto', maxWidth: 600, marginTop: 15 }} />

		<Modal size='tiny' open={shownModal === ShownModal.APPROVE_COMPLETE} onClose={bindClick(setShownModal)}>
			<Modal.Header>{payout.approved ? 'Completar' : 'Aprobar'} liquidación</Modal.Header>
			<Modal.Content>
				<Header text={`¿${payout.approved ? 'Completar' : 'Aprobar'} liquidación?`} subtext={`Marcará la liquidación como ${payout.approved ? 'completada' : 'aprobada'}`} style={{ paddingLeft: 0, paddingRight: 0, fontSize: 26 }} />
				<div style={{ textAlign: 'center' }}>
					{(payout.bank_account) ? 'Se agregará a la fila de liquidaciones pendientes a liquidar.' : 'Ya que la liquidación no es por transferencia se marcará la liquidación como completada.'}
				</div>
				<Table details style={{ marginTop: 15 }}>
					<Table.Head title='Información' titleSize='small' />
					<Table.Body data={[
						['ID', payout.payout_id],
						['Evento', payout.event_name],
						['Cantidad', addCommas(payout_total)],
						...(payout.date_scheduled ? [
							['Agendada', fromUnixLocal(payout.date_scheduled).toFormat('dd/MMM/yy')]
						] : []),
						['Creador', payout.user_creator_name],
					]} />
				</Table>
				{!!payout.approved && !!completeForm && <>
					<Checkbox checked={completeForm.completed && !completeForm.stp} label={'Esta liquidación ya se depositó'} onChange={onCompleteFormChange('completed')} disabled={completeForm.stp} style={{ marginTop: 15, display: 'block' }} />
					{ACCESS.stp && (
						<Checkbox checked={completeForm.stp && !completeForm.completed} label={'Descargar archivo STP'} onChange={onCompleteFormChange('stp')} disabled={completeForm.completed} style={{ marginTop: 5, display: 'block' }} />
					)}
				</>}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text={payout.approved ? 'Completar' : 'Aprobar'} color='black' onClick={payout.approved ? completePayout(false) : approvePayout} />
			</Modal.Actions>
		</Modal>
		<Modal size='tiny' open={shownModal === ShownModal.REJECT} onClose={bindClick(setShownModal)}>
			<Modal.Header>{payout.approved ? 'Cancelar' : 'Rechazar'} liquidación</Modal.Header>
			<Modal.Content>
				<Header text={`¿${payout.approved ? 'Cancelar' : 'Rechazar'} liquidación?`} subtext={`Marcará la liquidación como cancelada`} />
				<div style={{ textAlign: 'center' }}>
					{payout.user_creator === user.user_id ? <>
						Se rechazará la liquidación, ya que tu fuiste el que creó la liquidación <b>no se enviará ningún correo.</b>
					</> : (
						'Se rechazará la liquidación y se le enviará un correo con los comentarios ingresados a el creador de la liquidación.'
					)}
				</div>
				<Input textarea textareaRows={5} label='Comentarios' onChange={setRejectComments} value={rejectComments} style={{ marginTop: 10 }} />
				<Checkbox checked={modalConfirm} onChange={setModalConfirm} label="Esta liquidación no se ha realizado y se desea cancelar" />
				<Checkbox checked={true} label="Cancelar también los depositos de PDV asociados a esta liquidación" style={{ marginTop: 10 }} disabled />
				<Message list={modalPrompts} type='error' />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Regresar' basic onClick={bindClick(setShownModal)} />
				<Button text='Cancelar' color='red' onClick={rejectPayout} disabled={!modalConfirm} />
			</Modal.Actions>
		</Modal>
		<Modal size='mini' open={shownModal === ShownModal.DOWNLOAD_STP}>
			<Modal.Header>Descarga STP</Modal.Header>
			{shownModal === ShownModal.DOWNLOAD_STP && <Modal.Content>
				<div style={{ textAlign: 'center', fontSize: 16 }}>Se ha hecho la descarga de el archivo para subir a STP.</div>
				<Message type='warning' centered style={{ marginTop: 15 }}>
					<div className="header">Confirmar descarga</div>
					En cuanto se descargue el archivo y se verifique que no se tengan errores, se necesitará <b>confirmar la descarga</b> para marcar las liquidaciones como liquidadas y evitar cualquier tipo de doble deposito.
					<br />
					{loadingStp ? (
						<Loader inline size={30} style={{ marginTop: 15 }} />
					) : !modalPrompts || modalPrompts.length <= 0 ? (
						<Button color='black' onClick={completePayout(true)} disabled={modalPrompts && modalPrompts.length > 0} text='Confirmar descarga' style={{ marginTop: 10 }} />
					) : (
						<Button basic text='Reintentar descarga' style={{ marginTop: 15 }} color='black' onClick={showSTP} />
					)}
				</Message>
				<Message type='error' list={modalPrompts} />
				{(!modalPrompts || modalPrompts.length <= 0) && (
					<Button basic text='Reintentar descarga' disabled={loadingStp} size='tiny' style={{ display: 'block', width: 150, margin: 'auto', marginTop: 15 }} onClick={showSTP} />
				)}
			</Modal.Content>}
		</Modal>
		<Modal size='tiny' open={shownModal===ShownModal.RESET_PAYOUT} onClose={bindClick(setShownModal)}>
			<Modal.Header>Regresar a pendiente</Modal.Header>
			<Modal.Content>
				<Header text={'Reiniciar liquidación'} subtext={`Se regresará al estatus de la liquidación a pendiente`} style={{ paddingLeft: 0, paddingRight: 0, fontSize: 26 }} />
				<Message header='¡Alerta!' centered type='error'>
					Si ya se había realizado la transferencia o el pago de la liquidación, no se deberá de realizar esta acción
				</Message>
				<Checkbox checked={modalConfirm} onChange={setModalConfirm} label="Esta es la acción que deseo realizar" style={{ marginTop: 15 }} />
				<Message list={modalPrompts} type='error' />
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
				<Button text={t('common.continue')} color='black' disabled={!modalConfirm} onClick={resetPayout} />
			</Modal.Actions>
		</Modal>
		<DeleteAssetModal
			open={!!deletingAsset}
			asset={deletingAsset}
			data={{ payouts: [payout.payout_id] }}
			onClose={bindClick(setDeletingAsset)}
			onDeleted={asset_id=>{
				setPayout(p=>({
					...p,
					assets: p.assets.filter(a=>a.asset_id!==asset_id)
				}))
			}}
			tableData={[
				['Liquidación', payout.payout_id]
			]}
		/>
		<CreateDepositModal
			open={shownModal===ShownModal.VIEW_DEPOSIT}
			onClose={bindClick(setShownModal)}
			pdvId={viewing_deposit?.pdv_id}
			onSave={null}
			depositId={viewing_deposit?.deposit_id}
		/>
	</div>
}

export default PayoutView;